/**
 * The vocabulary every cost surface shares: the Internal Planung tab, the
 * customer's Auswertung, the public Auswertung and exposé, and both generated
 * PDFs.
 *
 * One concept, one German word. A surface that heads the same figure
 * differently reads as a different figure — the reason these strings cannot
 * live once per surface. Anything that is a field of an entity is NOT here;
 * it belongs in `FIELD_LABELS` and is read through `useField(key)`.
 *
 * Every member is a finished heading, rendered verbatim. A noun meant to be
 * built into a longer label belongs in `COST_TERMS` below — that split is
 * what keeps `Zusammensetzung der ${renovationCost}` from being
 * written: only a term may appear inside a template literal.
 */
import { ANCILLARY_COST_RATE } from '../../constants/pricing'

import { COMMON } from './common'
import { TERMS } from './terms'

/** Read by both all-in labels below, so the noun is written once. Private:
 *  no surface drops it into a slot of its own. */
const ALL_IN_TOTAL = 'Gesamtinvestition'

export const COST_LABELS = {
  /** Sum of the staff-entered positions. */
  baseCost: 'Basissanierung',
  /** What the customer's configurator picks add on top of the base. */
  selection: 'Ausgewählte Optionen',
  /** Base plus selection. */
  renovationCost: 'Sanierungskosten',
  /** Compact form of `baseCost`, naming the side a position row came from. */
  baseCostShort: 'Basis',
  totalNet: 'Gesamt netto',
  /** What the company keeps: the `MARGIN` blocks of a staged price, added up.
   *  Read back out of the former fee factor for a position frozen before the
   *  stages existed. */
  margin: 'Marge',
  /** What the state takes: the `VAT` blocks of a staged price. Spelled out
   *  rather than "MwSt." wherever a breakdown gives it a row of its own. */
  vat: 'Mehrwertsteuer',
  totalGross: 'Gesamt brutto',
  /** Stands in until at least one position carries a price. */
  pricePending: 'Wird ermittelt',
  /** Over the figure a public tile quotes: the property plus the fixed-price
   *  renovation. The Kaufnebenkosten are not in it — they fall on the
   *  property's own price and are stated where that is broken down. */
  purchaseInclRenovation: 'Kaufpreis inkl. Sanierung',
  nothingSelected: 'Nichts ausgewählt',
  /** The block holding everything that belongs to no single room. A property
   *  can be a flat, so this must not say "Haus". */
  building: TERMS.building,
  /** Line total of a position — quantity × flat rate. Not a field: no entity
   *  stores it. */
  lineTotal: COMMON.total,
  /** Where a position sits: the house, or one room. */
  place: 'Ort',
  /** The place filter's widest value. */
  allPlaces: 'Alle Orte',
  /** What one place's positions add up to. Never "Gesamt": the tab is headed
   *  by the project's total, and two figures called the same thing that are
   *  not the same figure is how the screen came to contradict itself. */
  positionsSum: 'Summe der Positionen',
  /** Beside a list of positions: how many, and what they add up to. */
  positionsTotal: (count: number, total: string) =>
    `${count} ${count === 1 ? COST_TERMS.position : COST_TERMS.positions} · ${total}`,
  /** Purchase price plus ancillary costs plus renovation — the figure the
   *  exposé, the Auswertung and the dashboard all quote. The noun must not
   *  change along that funnel. */
  allInTotal: ALL_IN_TOTAL,
  /** The Auswertung's variant: there the figure prices a configuration the
   *  visitor assembled from their own property data. The exposé keeps the
   *  bare heading — it states the same number again in a sticky bar too
   *  narrow for the qualifier, and one page must not spell it two ways. */
  allInTotalEstimate: `${ALL_IN_TOTAL} (Schätzung)`,
  ancillaryCosts: `Kaufnebenkosten (ca. ${ANCILLARY_COST_RATE * 100} %)`,
  /** An option that costs nothing extra, and one whose price is not settled
   *  yet. `formatOptionPrice` renders these and the CMS labels its price modes
   *  with them. */
  priceIncluded: 'Inklusive',
  priceOnRequest: 'Auf Anfrage',
  /** Some picks in the set carry no price yet, so the sum is a lower bound. */
  priceFrom: (value: string) => `ab ${value}`,
  vatIncluded: (rate: number) => `inkl. ${rate} % MwSt.`,
  netAmount: (value: string) => `${value} ${COST_TERMS.net}`,
} as const

/**
 * Nouns that surfaces build labels out of, in both numbers: a button says
 * "Position hinzufügen", a table is headed "Positionen".
 *
 * A term may only fill a slot that nothing agrees with — no article and no
 * adjective beside it, since those carry case, number and gender this table
 * cannot promise. "Alle zugeordneten Positionen werden gelöscht" stays a
 * literal in the surface's own file.
 */
export const COST_TERMS = {
  /** One `CostEntry` — a staff-entered line of the base renovation. */
  position: 'Position',
  positions: 'Positionen',
  /** Marks a figure as excluding VAT. */
  net: 'netto',
} as const

/**
 * How many positions, in how many projects — said by every refusal that lists
 * them, because a catalogue change reaches every project at once.
 *
 * One clause rather than one per dialog: the orphan confirmation and the reword
 * confirmation differ in what follows it, not in how they count, and written
 * out twice is how they come to count differently.
 */
export const positionsInProjects = (
  positions: number,
  projects: number,
): string =>
  `${positions === 1 ? `1 ${COST_TERMS.position}` : `${positions} ${COST_TERMS.positions}`} in ${
    projects === 1 ? '1 Projekt' : `${projects} Projekten`
  }`

/** Which side of the cost breakdown a position row came from. */
export const POSITION_SOURCE_LABELS = {
  heading: TERMS.source,
  base: COST_LABELS.baseCostShort,
  customer: TERMS.customer,
} as const

/**
 * The customer's own artifacts address them directly, so a concept the
 * Auswertung and the customer PDF both name gets its du-form here. Internal
 * surfaces keep the neutral wording above.
 */
const CUSTOMER_SUMMARY_TITLE = 'Deine Auswertung'

export const CUSTOMER_COST_LABELS = {
  selection: 'Deine Auswahl',
  summaryTitle: CUSTOMER_SUMMARY_TITLE,
  /**
   * The switch over the breakdown, and the question the download asks.
   *
   * The texts state what is owed and run to a paragraph each, so the screen
   * starts without them and the reader asks. The download asks the same thing
   * separately: a document is read away from the screen, and what is on the
   * screen at the time says nothing about what belongs in the file.
   */
  serviceDescriptions: {
    show: `${TERMS.serviceDescriptionPlural} anzeigen`,

    /** The one option the download carries. */
    pdfTitle: `${CUSTOMER_SUMMARY_TITLE} als PDF`,
    pdfInclude: `${TERMS.serviceDescriptionPlural} einschließen`,
    pdfSubmit: 'Herunterladen',
  },
} as const
